import { useState, useEffect } from "react";
import { Star, ExternalLink } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND, REAL_REVIEWS_EN, REAL_REVIEWS_AR } from "@/lib/constants";

const API = process.env.REACT_APP_BACKEND_URL;
const TRUNCATE_AT = 140;

const fmtWhen = (iso, lang) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(lang === "ar" ? "ar" : "en", { month: "short", year: "numeric" });
};

const ReviewCard = ({ review, readMoreLabel, readLessLabel, index }) => {
  const [expanded, setExpanded] = useState(false);
  const needsTruncate = review.full && review.full.length > TRUNCATE_AT;
  const shown = !needsTruncate || expanded ? review.full : `${review.full.slice(0, TRUNCATE_AT).trim()}…`;
  const dir = review.lang === "ar" ? "rtl" : review.lang === "en" ? "ltr" : undefined;
  return (
    <div
      dir={dir}
      className="relative rounded-2xl border border-white/10 bg-[#111] p-6 md:p-7"
      data-testid={`review-card-${index}`}
    >
      <div className="flex items-center gap-2 text-amber-400 mb-4">
        {Array.from({ length: review.rating || 5 }).map((_, k) => (
          <Star key={`${review.name}-star-${k}`} className="w-4 h-4 fill-current" />
        ))}
      </div>

      <p className="text-white/85 leading-relaxed">{shown}</p>

      {needsTruncate && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-sm text-[#e10600] font-semibold hover:underline"
          data-testid={`review-toggle-${index}`}
        >
          {expanded ? readLessLabel : readMoreLabel}
        </button>
      )}

      <div className="mt-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-white/15 to-white/5 border border-white/10 flex items-center justify-center font-display font-bold text-white/80">
          {(review.name || "?").charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="text-white font-semibold text-sm">{review.name}</p>
          <p className="text-white/45 text-xs">Google · {review.when || ""}</p>
        </div>
      </div>
    </div>
  );
};

const Reviews = () => {
  const { t, lang } = useLanguage();
  const [live, setLive] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setLive)
      .catch(() => {});
  }, []);

  const usingLive = Boolean(live && Array.isArray(live.reviews) && live.reviews.length > 0);
  const reviews = usingLive
    ? live.reviews.map((r) => ({
        name: r.author,
        full: r.text,
        rating: r.rating,
        when: fmtWhen(r.time, lang),
        lang: r.lang,
        timeIso: r.time,
      }))
    : lang === "ar"
    ? REAL_REVIEWS_AR
    : REAL_REVIEWS_EN;

  // Keep JSON-LD in exact sync with the pushed data. The review markup is built
  // 100% from /api/reviews; in fallback mode (nothing pushed) we inject nothing —
  // the static reviews stay visible for humans but are never claimed as markup.
  useEffect(() => {
    if (!usingLive) return;
    const node = {
      "@context": "https://schema.org",
      "@type": "AutoBodyShop",
      "@id": "https://maps.app.goo.gl/LAfCJJk72dAFhYmR7",
      name: "Dent Xpress",
      url: "https://dentxpress.com/",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: String(live.rating),
        bestRating: "5",
        ratingCount: String(live.total),
      },
      review: live.reviews.map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.author },
        reviewRating: { "@type": "Rating", ratingValue: String(r.rating || 5), bestRating: "5" },
        reviewBody: r.text,
        ...(typeof r.time === "string" && r.time.length >= 10
          ? { datePublished: r.time.slice(0, 10) }
          : {}),
      })),
    };
    let el = document.getElementById("dx-reviews-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "dx-reviews-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(node);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  return (
    <section className="relative py-20 md:py-28 bg-[#0c0c0c]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-[11px] tracking-[0.25em] uppercase font-display text-white/70">
              <span className="w-1.5 h-1.5 rounded-full bg-[#e10600]" />
              {t.reviews.eyebrow}
            </div>
            <h2 className="mt-4 font-display font-black text-3xl md:text-5xl tracking-tight">
              {t.reviews.title}
            </h2>
            <p className="mt-3 text-white/60 max-w-xl">{t.reviews.subtitle}</p>
            {usingLive && (
              <div className="mt-4 flex items-center gap-3" data-testid="reviews-summary">
                <span className="font-display font-black text-3xl text-white">
                  {Number(live.rating).toFixed(1)}
                </span>
                <span className="flex items-center gap-1 text-amber-400">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star
                      key={k}
                      className={`w-4 h-4 ${k < Math.round(live.rating) ? "fill-current" : "opacity-30"}`}
                    />
                  ))}
                </span>
                <span className="text-white/55 text-sm">
                  {lang === "ar"
                    ? `${live.total} تقييم على Google`
                    : `${live.total} reviews on Google`}
                </span>
              </div>
            )}
          </div>
          <a
            href={BRAND.mapsLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black hover:bg-white/90 font-bold transition self-start"
            data-testid="reviews-maps-btn"
          >
            {t.reviews.cta}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        <div
          className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-xs text-emerald-300"
          data-testid="reviews-real-note"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          {t.reviews.verifiedBadge}
        </div>

        <div
          className={`grid grid-cols-1 gap-4 md:gap-5 ${
            reviews.length === 2
              ? "md:grid-cols-2"
              : reviews.length >= 4
              ? "md:grid-cols-2 lg:grid-cols-4"
              : "md:grid-cols-3"
          }`}
        >
          {reviews.map((r, i) => (
            <ReviewCard
              key={`${r.name}-${i}`}
              index={i}
              review={r}
              readMoreLabel={t.common.readMore}
              readLessLabel={t.common.readLess}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
